import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';

import {
  MemberQuickViewContent,
  MemberQuickViewLoading,
  displayMemberName,
  type MemberQuickViewRecord,
} from '@/components/shared/member-quick-view-content';
import {
  canChooseExtendedMemberPreview,
  useMemberQuickViewPreviewMode,
} from '@/components/shared/member-quick-view-preview-mode';
import { MemberQuickViewPreviewToggle } from '@/components/shared/member-quick-view-preview-toggle';
import { ErrorState, PermissionDeniedState } from '@/components/shared/states';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { hasPermission, PERMISSIONS, useCurrentMembership } from '@/lib/permissions';
import { queryKeys } from '@/lib/query-client';
import { useChurchStore } from '@/stores/church-store';
import { cn } from '@/utils/cn';

export type { MemberQuickViewRecord } from '@/components/shared/member-quick-view-content';

export interface MemberQuickViewDialogProps {
  memberId: string | null;
  previewName?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const resolveErrorStatus = (error: unknown): number | null =>
  error instanceof ApiError ? error.status : null;

export const MemberQuickViewDialog = ({
  memberId,
  previewName,
  open,
  onOpenChange,
}: MemberQuickViewDialogProps) => {
  const { t } = useTranslation('people');
  const { t: tCommon } = useTranslation();
  const membership = useCurrentMembership();
  const churchId = useChurchStore((state) => state.church?.id ?? null);
  const isModuleEnabled = useChurchStore((state) => state.isModuleEnabled);

  const canViewContact = hasPermission(membership, PERMISSIONS.PEOPLE_VIEW_CONTACT);
  const canViewProfile =
    isModuleEnabled('people') && hasPermission(membership, PERMISSIONS.PEOPLE_VIEW);
  const canChooseExtended = canChooseExtendedMemberPreview(membership);
  const { extendedPreview, setExtendedPreview } = useMemberQuickViewPreviewMode(churchId);
  const previewMode = canChooseExtended && extendedPreview ? 'extended' : 'basic';

  const member = useQuery({
    queryKey: queryKeys.detail(churchId, 'people/members', memberId ?? ''),
    queryFn: () => api.get<MemberQuickViewRecord>(`/people/members/${memberId}`),
    enabled: open && Boolean(memberId),
  });

  const row = member.data;
  const name = row ? displayMemberName(row) : (previewName?.trim() || t('members.quickView.title'));
  const titledName = row?.title ? `${row.title} ${name}`.trim() : name;
  const typeLabel = row ? t(`members.type.${row.memberType}`) : null;
  const statusLabel = row ? t(`members.status.${row.status}`) : null;
  const description = typeLabel && statusLabel ? `${typeLabel} · ${statusLabel}` : t('members.quickView.title');
  const errorStatus = resolveErrorStatus(member.error);

  let body = null;
  if (member.isLoading) {
    body = <MemberQuickViewLoading />;
  } else if (errorStatus === 403) {
    body = (
      <PermissionDeniedState
        title={t('members.quickView.deniedTitle')}
        body={t('members.quickView.deniedBody')}
        className="py-6"
      />
    );
  } else if (errorStatus === 404) {
    body = <ErrorState title={t('members.quickView.unavailable')} className="py-6" />;
  } else if (member.error) {
    body = (
      <ErrorState
        title={t('members.quickView.loadError')}
        onRetry={() => void member.refetch()}
        retryLabel={tCommon('action.retry')}
        className="py-6"
      />
    );
  } else if (row) {
    body = (
      <MemberQuickViewContent
        row={row}
        name={name}
        canViewContact={canViewContact}
        previewMode={previewMode}
      />
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="md" closeLabel={tCommon('action.close')}>
        <DialogHeader>
          <DialogTitle>{titledName}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <DialogBody contentClassName="flex flex-col gap-6">{body}</DialogBody>

        <DialogFooter
          className={cn(
            canChooseExtended && open && memberId ? 'sm:items-center sm:justify-between' : undefined,
          )}
        >
          {canChooseExtended && open && memberId ? (
            <MemberQuickViewPreviewToggle
              id="member-preview-dialog-extended"
              checked={extendedPreview}
              onCheckedChange={setExtendedPreview}
            />
          ) : null}
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              {tCommon('action.close')}
            </Button>
            {canViewProfile && row ? (
              <Button asChild>
                <Link to={`/people/${row.id}`} onClick={() => onOpenChange(false)}>
                  {t('members.quickView.viewProfile')}
                </Link>
              </Button>
            ) : null}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
